import { Box, Button, ButtonGroup, Container, Grid, List, ListItem, ListItemText, Paper, Popover, Snackbar, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, useMediaQuery, useTheme } from "@mui/material";
import { Link, useNavigate, useParams } from "react-router-dom";
import { mergeQuotes, Quote, QuoteEditPreviewButton } from "./Quotes";
import { Fragment, useContext, useEffect, useRef, useState } from "react";
import { EkasiServeAppBar, formatPercentage, selectAllOnFocus, SetState } from "./Common";
import { ArrowDropDownIcon, DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { aggregateUnitCounts, ItemDelivery, ItemDeliveryDate } from "./QuotesDelivery";
import { CreditHTML, DeliveryHTML, DownloadPDFButton, InvoiceHTML, pdfFileName, QuoteHTML } from "./PDFs/TemplatesGen";
import { Client } from "./Clients";
import { ClientDetails } from "./ClientDetails";
import { calculateFinancialSummary, DefaultFinancialSummary, FinancialSummary, Item, mapDbItemsToLocal } from "./Items";
import { Request, RequestsTable } from "./Requests";
import { Add } from "@mui/icons-material";
import { UserContext, userIsEmployeeOrAdmin } from "./Users";
import { Company } from "./Companies";


interface QuoteDelivered {
    quote: Quote
    item_delivery_date: ItemDeliveryDate
}

export function mergeQuotesDelivered(old: Array<QuoteDelivered>, toAdd: Array<QuoteDelivered>): Array<QuoteDelivered> {
    var result = [...old];
    for (let q of toAdd) {
        if (result.find((p) => p.quote.quote_no === q.quote.quote_no && p.item_delivery_date.iteration === q.item_delivery_date.iteration) === undefined) {
            result.push(q);
        }
    }
    return result;
}

export function mergeRequests(old: Array<Request>, toAdd: Array<Request>): Array<Request> {
    var result = [...old];
    for (let q of toAdd) {
        if (result.find((p) => p.id === q.id) === undefined) {
            result.push(q);
        }
    }
    return result;
}

const saveQuote = async (quote: Quote, setQuotes: SetState<Array<Quote>>, setSnackbar: SetState<string>) => {
    const response = await fetch("/api/v1/quotes", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ quote: quote, detail: {} })
    });
    const data = await response.json();
    if (data.error) {
        setSnackbar(data.error);
    } else {
        setQuotes(quotes => quotes.map(q => q.quote_no === quote.quote_no ? quote : q)) 
        setSnackbar("Quote Saved");
    }
}

const updateDeliveredItemsDate = async (quoteNo: string, iteration: number, taxDate: string, paid: number, setSnackbar: SetState<string>) => {
    const response = await fetch("/api/v1/items_delivered_dates", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({"quote_no": quoteNo, "iteration": iteration, "paid": paid, "tax_date": taxDate})
    });
    const data = await response.json();
    if (data.error) {
        setSnackbar(data.error);
    } else {
        setSnackbar("Delivery Saved");
    }
}

export function QuotesCancelledTable({ quotes, setQuotes, setSnackbar } : { quotes: Array<Quote>, setQuotes: SetState<Array<Quote>>, setSnackbar: SetState<string> } ) {
    const auth = useContext(UserContext);
    return (
        <Grid container spacing={2} sx={{ alignItems: "center" }}>
            <Grid size={12}>
                <Paper>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell width="20%">Quote No.</TableCell>
                                    <TableCell width="20%">Quote Date</TableCell>
                                    <TableCell width="30%">Title</TableCell>
                                    {userIsEmployeeOrAdmin(auth) && <TableCell width="30%"></TableCell>}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {quotes.map((row, idx) => (
                                    <TableRow key={"cancelled: " + row.quote_no + idx}>
                                        <TableCell>
                                            <QuoteEditPreviewButton quote_no={row.quote_no} iconOnly={false}/>
                                        </TableCell>
                                        <TableCell>{row.quote_date}</TableCell>
                                        <TableCell>{row.quote_title}</TableCell>
                                        {userIsEmployeeOrAdmin(auth) && <TableCell>
                                            <Button variant="contained" onClick={(ev) => saveQuote({ ...row, cancelled: row.cancelled === 1 ? 0 : 1 }, setQuotes, setSnackbar)} fullWidth>
                                                { row.cancelled ? "Un-cancel" : "Cancel Order" }
                                            </Button>
                                        </TableCell>}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

            </Grid>
        </Grid>
    )
}

export function RequestsQuoteTable({ requests } : { requests: Array<Request> } ) {
    const navigate = useNavigate();
    return (
        <Grid container spacing={2} sx={{ alignItems: "center" }}>
            <Grid size={12}>
                <Paper>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell width="30%">Title</TableCell>
                                    <TableCell width="50%">Comments</TableCell>
                                    <TableCell width="20%"></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {requests.map((row, idx) => (
                                    <TableRow key={"requests: "+ row.id + idx}>
                                        <TableCell>{row.title}</TableCell>
                                        <TableCell>{row.comments}</TableCell>
                                        <TableCell>
                                            <Button variant="contained" onClick={() => navigate("/quotes/0", { state: { requestIdText: row.id.toString() } })} fullWidth>
                                                Make Quote
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

            </Grid>
        </Grid>
    )
}

function ExportPopover({ quote, setSnackbar, iteration = undefined }: { quote: Quote, setSnackbar: SetState<string>, iteration: number | undefined }) {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    const [company, setCompany] = useState<Partial<Company>>({});
    const [details, setDetails] = useState<Partial<ClientDetails>>({});
    const [client, setClient] = useState<Partial<Client>>({});
    const [items, setItems] = useState<Array<Item>>([]);
    const [itemDeliveryDates, setItemDeliveryDates] = useState<Array<ItemDeliveryDate>>([]);
    const [itemDeliveries, setItemDeliveries] = useState<Array<Array<ItemDelivery>>>([]);
    const [aggregateUnits, setAggregateUnits] = useState<Array<ItemDelivery>>([]);
    const [financialSummary, setFinancialSummary] = useState<FinancialSummary>(DefaultFinancialSummary);
    const [fetchedData, setFetchedData] = useState(false);


    const fetchDetails = async () => {
        const quoteResponse = await fetch("/api/v1/quotes/" + encodeURIComponent(quote.quote_no || ""));
        const quoteData = await quoteResponse.json();
        if (quoteData.result) {
            quoteData.result.quote.service_fee_text = formatPercentage(quoteData.result.quote.service_fee || 250);
            quoteData.result.quote.vat_rate_text = formatPercentage(quoteData.result.quote.vat_rate || 1500);
        }
        const _client = quoteData?.result?.client || {}; 
        setClient(quoteData?.result?.client || {});
        setDetails(quoteData?.result?.client_detail || {});
        if (quoteData.error) {
            setSnackbar(quoteData.error);
            return;
        }

        const companyResponse = await fetch("/api/v1/companies/" + encodeURIComponent(_client.company_id || "0"));
        const companyData = await companyResponse.json();
        setCompany(companyData.result || {});
        if (companyData.error) {
            setSnackbar(companyData.error);
        }

        const itemsResponse = await fetch("/api/v1/items/" + encodeURIComponent(quote.quote_no || ""));
        const itemsData = await itemsResponse.json();
        const _items = itemsData?.result?.map(mapDbItemsToLocal) || []
        setItems(_items);
        if (itemsData.error) {
            setSnackbar(itemsData.error);
            return;
        }

        const itemsDeliveredResponse = await fetch("/api/v1/items_delivered/" + encodeURIComponent(quote.quote_no));
        const itemsDeliveredData = await itemsDeliveredResponse.json();
        setItemDeliveries(itemsDeliveredData?.result?.items || []);
        setItemDeliveryDates(itemsDeliveredData?.result?.dates || []);
        const _itemDeliveries = itemsDeliveredData?.result?.items || [];
        if (itemsDeliveredData.error) {
            setSnackbar(itemsDeliveredData.error);
            return;
        };
        const agg = aggregateUnitCounts(_items, _itemDeliveries, iteration !== undefined ? iteration : 999999);
        setAggregateUnits(agg);
        
        calculateFinancialSummary(quote, _items, setFinancialSummary, _itemDeliveries[iteration !== undefined ? iteration : _itemDeliveries.length - 1] || [], setItemDeliveryDates);

        setFetchedData(true);
    };

    const it = iteration !== undefined ? iteration : itemDeliveries.length - 1;
    return (
        <Fragment>
            <Button sx={{ width: 32 }} aria-describedby="Products" onClick={(ev) => { setFetchedData(false); fetchDetails(); setAnchorEl(ev.currentTarget)} } variant="contained">
                <ArrowDropDownIcon />
            </Button>
            <Popover
                id="ExportQuote"
                open={anchorEl !== null}
                onClose={() => setAnchorEl(null)}
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
                <List>
                    <ListItem>
                        {fetchedData && <DownloadPDFButton content={QuoteHTML(quote, details, client, company, items, financialSummary)} filename={pdfFileName(quote, "Quote")}>
                            <ListItemText>{!details.id ? "Loading..." : "Export Quote"}</ListItemText>
                        </DownloadPDFButton>}
                    </ListItem>
                    <ListItem>
                        {fetchedData && <DownloadPDFButton content={DeliveryHTML(quote, details, client, items, aggregateUnits)} filename={pdfFileName(quote, "Delivery Note")}>
                            <ListItemText>{!details.id ? "Loading..." : "Export Delivery Note"}</ListItemText>
                        </DownloadPDFButton>}
                    </ListItem>
                    <ListItem>
                        {fetchedData && <DownloadPDFButton content={InvoiceHTML(quote, details, client, company, items, financialSummary, itemDeliveries[it], itemDeliveryDates[it])} filename={pdfFileName(quote, "Invoice", itemDeliveryDates[it])}>
                            <ListItemText>{!details.id ? "Loading..." : "Export Tax Note"}</ListItemText>
                        </DownloadPDFButton>}
                    </ListItem>
                    <ListItem>
                        {fetchedData && <DownloadPDFButton content={CreditHTML(quote, details, client, company, items, financialSummary, itemDeliveries[it], itemDeliveryDates[it])} filename={pdfFileName(quote, "Credit", itemDeliveryDates[it])}>
                            <ListItemText>{!details.id ? "Loading..." : "Export Credit Note"}</ListItemText>
                        </DownloadPDFButton>}
                    </ListItem>
                </List>
            </Popover>
        </Fragment>
    )
}

export function QuotesPendingTable({ quotes, setQuotes, setSnackbar } : { quotes: Array<Quote>, setQuotes: SetState<Array<Quote>>, setSnackbar: SetState<string> } ) {
    const auth = useContext(UserContext);
    return (
        <Grid container spacing={2} sx={{ alignItems: "center" }}>
            <Grid size={12}>
                <Paper>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell width="15%">Quote No.</TableCell>
                                    <TableCell width="20%">Quote Date</TableCell>
                                    <TableCell width="20%">PO Date</TableCell>
                                    <TableCell width="20%">PO Number</TableCell>
                                    {userIsEmployeeOrAdmin(auth) && <TableCell width="20%"></TableCell>}
                                    {userIsEmployeeOrAdmin(auth) && <TableCell width="5%"></TableCell>}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {quotes.map((row, idx) => (
                                    <TableRow key={"pending: "+ row.quote_no + idx}>
                                        <TableCell>
                                            <QuoteEditPreviewButton quote_no={row.quote_no} iconOnly={false}/>
                                        </TableCell>
                                        <TableCell>{row.quote_date}</TableCell>
                                        <TableCell>
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DatePicker
                                                    label="PO Date"
                                                    slotProps={{ 
                                                        textField: { 
                                                            error: (row.po_date || "") === "",
                                                            fullWidth: true, 
                                                            readOnly: !userIsEmployeeOrAdmin(auth) 
                                                        } 
                                                    }}
                                                    onChange={(value) => setQuotes((quotes) => quotes.map(q => q.quote_no === row.quote_no ? { ...row, po_date: value?.format("YYYY-MM-DD") || "" } : q)) }
                                                    onAccept={(value) => saveQuote({ ...row, po_date: value?.format("YYYY-MM-DD") || "" }, setQuotes, setSnackbar) }
                                                    value={row.po_date === undefined || row.po_date === "" ? dayjs() : dayjs(row.po_date, "YYYY-MM-DD")} />
                                            </LocalizationProvider>
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                id="po_no"
                                                label="PO Number"
                                                variant="outlined"
                                                error={!row.po_no}
                                                slotProps={{ input: { readOnly: !userIsEmployeeOrAdmin(auth) } }}
                                                onFocus={selectAllOnFocus}
                                                onChange={(e) => setQuotes(quotes => quotes.map(q => q.quote_no === row.quote_no ? { ...row, po_no: e.target.value } : q))}
                                                onKeyUp={(e: any) => e.key === "Enter" ? saveQuote({ ...row, po_no: e.target.value }, setQuotes, setSnackbar) : 0}
                                                fullWidth
                                                value={row.po_no || ""} />
                                        </TableCell>
                                        {userIsEmployeeOrAdmin(auth) && <TableCell>
                                            <Button variant="contained" disabled={(row.po_no || "") !== ""} onClick={(ev) => saveQuote({ ...row, cancelled: row.cancelled === 1 ? 0 : 1 }, setQuotes, setSnackbar)} fullWidth>
                                                { row.cancelled ? "Un-cancel" : "Cancel" }
                                            </Button>
                                        </TableCell>}
                                        {userIsEmployeeOrAdmin(auth) && <TableCell>
                                            <ExportPopover quote={row} setSnackbar={setSnackbar} iteration={undefined}  />
                                        </TableCell>}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

            </Grid>
        </Grid>
    )
}

export function QuotesProcessingTable({ quotes, setQuotes, setSnackbar } : { quotes: Array<Quote>, setQuotes: SetState<Array<Quote>>, setSnackbar: SetState<string> } ) {
    const auth = useContext(UserContext);
    return (
        <Grid container spacing={2} sx={{ alignItems: "center" }}>
            <Grid size={12}>
                <Paper>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell width="15%">Quote No.</TableCell>
                                    <TableCell width="15%">Quote Date</TableCell>
                                    <TableCell width="25%">Title</TableCell>
                                    {userIsEmployeeOrAdmin(auth) && <TableCell width="15%"></TableCell>}
                                    {userIsEmployeeOrAdmin(auth) && <TableCell width="15%"></TableCell>}
                                    {userIsEmployeeOrAdmin(auth) && <TableCell width="5%"></TableCell>}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {quotes.map((row, idx) => (
                                    <TableRow key={"pending: "+ row.quote_no + idx}>
                                        <TableCell>
                                            <QuoteEditPreviewButton quote_no={row.quote_no} iconOnly={false}/>
                                        </TableCell>
                                        <TableCell>{row.quote_date}</TableCell>
                                        <TableCell>{row.quote_title}</TableCell>
                                        {userIsEmployeeOrAdmin(auth) && <TableCell>
                                            <Button variant="contained" onClick={
                                                (ev) => saveQuote({ ...row, confirmed_for_delivery: row.confirmed_for_delivery === 1 ? 0 : 1 }, setQuotes, setSnackbar)} fullWidth>
                                                { row.confirmed_for_delivery ? "Un-confirm" : "Confirm" }
                                            </Button>
                                        </TableCell>}
                                        {userIsEmployeeOrAdmin(auth) && <TableCell>
                                            <Button variant="contained" onClick={
                                                (ev) => saveQuote({ ...row, cancelled: row.cancelled === 1 ? 0 : 1 }, setQuotes, setSnackbar)} fullWidth>
                                                { row.cancelled ? "Un-cancel" : "Cancel" }
                                            </Button>
                                        </TableCell>}
                                        {userIsEmployeeOrAdmin(auth) && <TableCell>
                                            <ExportPopover quote={row} setSnackbar={setSnackbar} iteration={undefined} />
                                        </TableCell>}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

            </Grid>
        </Grid>
    )
}

export function QuotesDeliveryTable({ quotes, taxDates, setTaxDates, setSnackbar } : { quotes: Array<Quote>, taxDates: {[x: string]: string}, setTaxDates: SetState<{[x: string]: string}>, setSnackbar: SetState<string> } ) {
    const updateDeliveredItems = async (quoteNo: string, taxDate: string) => {
        const response = await fetch("/api/v1/items_delivered/full?quote_no=" + encodeURIComponent(quoteNo) + "&tax_date=" + encodeURIComponent(taxDate), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({})
        });
        const data = await response.json();
        if (data.error) {
            setSnackbar(data.error);
        } else {
            setSnackbar("Delivery Saved");
        }
    }
    const auth = useContext(UserContext);
    return (
        <Grid container spacing={2} sx={{ alignItems: "center" }}>
            <Grid size={12}>
                <Paper>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell width="30%">Quote No.</TableCell>
                                    <TableCell width="30%">PO Date</TableCell>
                                    {userIsEmployeeOrAdmin(auth) && <TableCell width="20%">Fully Delivered</TableCell>}
                                    {userIsEmployeeOrAdmin(auth) && <TableCell width="15%"></TableCell>}
                                    {userIsEmployeeOrAdmin(auth) && <TableCell width="5%"></TableCell>}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {quotes.map((row, idx) => (
                                    <TableRow key={"delivery: " + row.quote_no + idx}>
                                        <TableCell>
                                            <QuoteEditPreviewButton quote_no={row.quote_no} iconOnly={false}/>
                                        </TableCell>
                                        <TableCell>{row.po_date}</TableCell>
                                        {userIsEmployeeOrAdmin(auth) && <TableCell>
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DatePicker
                                                    label="Tax Date"
                                                    slotProps={{ textField: { fullWidth: true } }}
                                                    onChange={(value) => setTaxDates(dates => ({ ...dates, [row.quote_no]: value?.format("YYYY-MM-DD") || "" }) ) }
                                                    onAccept={(value) => updateDeliveredItems(row.quote_no, value?.format("YYYY-MM-DD") || "")}
                                                    value={taxDates[row.quote_no] ? dayjs(taxDates[row.quote_no], "YYYY-MM-DD") : dayjs()} />
                                            </LocalizationProvider>
                                        </TableCell>}
                                        {userIsEmployeeOrAdmin(auth) && <TableCell>
                                            <Button variant="contained" disabled={taxDates[row.quote_no] !== undefined} component={Link} to={"/quotes/delivery/" + encodeURIComponent(row.quote_no)} fullWidth>
                                                Delivery
                                            </Button>
                                        </TableCell>}
                                        {userIsEmployeeOrAdmin(auth) && <TableCell>
                                            <ExportPopover quote={row} setSnackbar={setSnackbar} iteration={undefined} />
                                        </TableCell>}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

            </Grid>
        </Grid>
    )
}

export function QuotesPartialTable({ quotes, setQuotes, taxDates, setTaxDates, setSnackbar } : { quotes: Array<Quote>, setQuotes: SetState<Array<Quote>>, taxDates: {[x: string]: string}, setTaxDates: SetState<{[x: string]: string}>, setSnackbar: SetState<string> } ) {
    const updateDeliveredItems = async (quoteNo: string, taxDate: string) => {
        const response = await fetch("/api/v1/items_delivered/full?quote_no=" + encodeURIComponent(quoteNo) + "&tax_date=" + encodeURIComponent(taxDate), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({})
        });
        const data = await response.json();
        if (data.error) {
            setSnackbar(data.error);
        } else {
            setSnackbar("Delivery Saved");
        }
    }
    const auth = useContext(UserContext);
    return (
        <Grid container spacing={2} sx={{ alignItems: "center" }}>
            <Grid size={12}>
                <Paper>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell width="20%">Quote No.</TableCell>
                                    <TableCell width="20%">PO Date</TableCell>
                                    {userIsEmployeeOrAdmin(auth) && <TableCell width="20%">Fully Delivered</TableCell>}
                                    {userIsEmployeeOrAdmin(auth) && <TableCell width="20%"></TableCell>}
                                    <TableCell width="15%"></TableCell>
                                    {userIsEmployeeOrAdmin(auth) && <TableCell width="5%"></TableCell>}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {quotes.map((row, idx) => (
                                    <TableRow key={"delivery: " + row.quote_no + idx}>
                                        <TableCell>
                                            <QuoteEditPreviewButton quote_no={row.quote_no} iconOnly={false}/>
                                        </TableCell>
                                        <TableCell>{row.po_date}</TableCell>
                                        {userIsEmployeeOrAdmin(auth) && <TableCell>
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DatePicker
                                                    label="Tax Date"
                                                    slotProps={{ textField: { fullWidth: true } }}
                                                    onChange={(value) => setTaxDates(dates => { var newDates = { ...dates, [row.quote_no]: value?.format("YYYY-MM-DD") || "" }; return newDates; } ) }
                                                    onAccept={(value) => updateDeliveredItems(row.quote_no, value?.format("YYYY-MM-DD") || "")}
                                                    value={taxDates[row.quote_no] ? dayjs(taxDates[row.quote_no], "YYYY-MM-DD") : dayjs()} />
                                            </LocalizationProvider>
                                        </TableCell>}
                                        {userIsEmployeeOrAdmin(auth) && <TableCell>
                                            <Button variant="contained" onClick={
                                                (ev) => saveQuote({ ...row, confirmed_for_delivery: row.confirmed_for_delivery === 1 ? 0 : 1 }, setQuotes, setSnackbar)} fullWidth>
                                                { row.confirmed_for_delivery ? "Un-confirm" : "Confirm" }
                                            </Button>
                                        </TableCell>}
                                        <TableCell>
                                            <Button variant="contained" disabled={taxDates[row.quote_no] !== undefined} component={Link} to={"/quotes/delivery/" + encodeURIComponent(row.quote_no)} fullWidth>
                                                Delivery
                                            </Button>
                                        </TableCell>
                                        {userIsEmployeeOrAdmin(auth) && <TableCell>
                                            <ExportPopover quote={row} setSnackbar={setSnackbar} iteration={undefined} />    
                                        </TableCell>}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

            </Grid>
        </Grid>
    )
}

export function QuotesUnpaidTable({ quotesDelivered, setQuotesDelivered, setSnackbar } : { quotesDelivered: Array<QuoteDelivered>, setQuotesDelivered: SetState<Array<QuoteDelivered>>, setSnackbar: SetState<string> } ) {
    const auth = useContext(UserContext);
    const downloadStatement = async () => {
        const response = await fetch("/api/v1/quotes-statement");
        const data = await response.blob();
        var a = document.createElement("a");
        a.href = window.URL.createObjectURL(data);
        a.download = "statement.xlsx";
        a.click();
    }

    return (
        <Grid container spacing={2} sx={{ alignItems: "center" }}>
            <Grid size={12}>
                <Paper>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell width="30%">Quote No.</TableCell>
                                    <TableCell width="30%">Tax Date</TableCell>
                                    {userIsEmployeeOrAdmin(auth) && <TableCell width="35%">
                                        <Button variant="contained" onClick={() => downloadStatement()} fullWidth>
                                            Download Statement
                                        </Button>
                                    </TableCell>}
                                    {userIsEmployeeOrAdmin(auth) && <TableCell width="5%"></TableCell>}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {quotesDelivered.map((row, idx) => (
                                    <TableRow key={"unpaid: " + row.quote.quote_no + (row.item_delivery_date.iteration > 0 ? " - "+ row.item_delivery_date.iteration : "") + idx}>
                                        <TableCell>
                                            <QuoteEditPreviewButton quote_no={row.quote.quote_no} suffix={row.item_delivery_date.iteration > 0 ? " - "+ row.item_delivery_date.iteration : ""} iconOnly={false}/>
                                        </TableCell>
                                        <TableCell>{row.item_delivery_date.tax_date}</TableCell>
                                        {userIsEmployeeOrAdmin(auth) && <TableCell>
                                            <Button 
                                                variant="contained"
                                                color={(row.item_delivery_date.paid === 1)  ? "error" : "primary"}
                                                onClick={(ev) => {
                                                    setQuotesDelivered(quotes => quotes.map((it, jdx) => idx === jdx ? { ...it, item_delivery_date: { ...it.item_delivery_date, paid: it.item_delivery_date.paid === 0 ? 1 : 0 } } : it));
                                                    updateDeliveredItemsDate(row.quote.quote_no, row.item_delivery_date.iteration, row.item_delivery_date.tax_date, row.item_delivery_date.paid === 0 ? 1 : 0, setSnackbar);
                                                }} fullWidth>
                                                    { row.item_delivery_date.paid ? "Unmark Paid" : "Mark Paid" }
                                            </Button>
                                        </TableCell>}
                                        {userIsEmployeeOrAdmin(auth) && <TableCell>
                                            <ExportPopover quote={row.quote} setSnackbar={setSnackbar} iteration={row.item_delivery_date.iteration} />
                                        </TableCell>}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

            </Grid>
        </Grid>
    )
}

export function QuotesPaidTable({ quotesDelivered, setQuotesDelivered, setSnackbar } : { quotesDelivered: Array<QuoteDelivered>, setQuotesDelivered: SetState<Array<QuoteDelivered>>, setSnackbar: SetState<string> } ) {
    const auth = useContext(UserContext);
    return (
        <Grid container spacing={2} sx={{ alignItems: "center" }}>
            <Grid size={12}>
                <Paper>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell width="30%">Quote No.</TableCell>
                                    <TableCell width="30%">Tax Date</TableCell>
                                    {userIsEmployeeOrAdmin(auth) && <TableCell width="35%"></TableCell>}
                                    {userIsEmployeeOrAdmin(auth) && <TableCell width="5%"></TableCell>}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {quotesDelivered.map((row, idx) => (
                                    <TableRow key={"paid: " + row.quote.quote_no + idx}>
                                        <TableCell>
                                            <QuoteEditPreviewButton quote_no={row.quote.quote_no} suffix={row.item_delivery_date.iteration > 0 ? " - "+ row.item_delivery_date.iteration : ""} iconOnly={false}/>
                                        </TableCell>
                                        <TableCell>{row.item_delivery_date.tax_date}</TableCell>
                                        {userIsEmployeeOrAdmin(auth) && <TableCell>
                                            <Button 
                                                variant="contained"
                                                color={row.item_delivery_date.paid === 0  ? "error" : "primary"}
                                                onClick={(ev) => {
                                                    setQuotesDelivered(quotes => quotes.map((it, jdx) => idx === jdx ? { ...it, item_delivery_date: { ...it.item_delivery_date, paid: it.item_delivery_date.paid === 0 ? 1 : 0 } } : it));
                                                    updateDeliveredItemsDate(row.quote.quote_no, row.item_delivery_date.iteration, row.item_delivery_date.tax_date, row.item_delivery_date.paid === 0 ? 1 : 0, setSnackbar);
                                                }} fullWidth>
                                                    { row.item_delivery_date.paid ? "Unmark Paid" : "Mark Paid" }
                                            </Button>
                                        </TableCell>}
                                        {userIsEmployeeOrAdmin(auth) && <TableCell>
                                            <ExportPopover quote={row.quote} setSnackbar={setSnackbar} iteration={row.item_delivery_date.iteration} />
                                        </TableCell>}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

            </Grid>
        </Grid>
    )
}

export enum StatusFlowState {
    pending = "pending",
    cancelled = "cancelled",
    requests = "requests",
    processing = "processing",
    delivery = "delivery",
    partially = "partially",
    unpaid = "unpaid",
    paid = "paid",
    delivered = "delivered"
}

export const StatusFlowPage = () => {
    const navigate = useNavigate();
    const { tab } = useParams();
    const [snackbar, setSnackbar] = useState("");
    const auth = useContext(UserContext);

    const [oldSelectedTab, setOldSelectedTab] = useState<StatusFlowState>(StatusFlowState.cancelled);
    const selectedTab = StatusFlowState[(tab || (userIsEmployeeOrAdmin(auth) ? "pending" : "requests")) as keyof typeof StatusFlowState];

    const [quotes, setQuotes] = useState<Array<Quote>>([]);
    const [quotesDelivered, setQuotesDelivered] = useState<Array<QuoteDelivered>>([]);
    const [taxDates, setTaxDates] = useState<{[x: string]: string}>({});
    const [requests, setRequests] = useState<Array<Request>>([]);
    const [offset, setOffset] = useState(0);
    const limit = 20;
    const scrollLimitFound = useRef(false);

    useEffect(() => {
        const handleScroll = () => {
            if (window.innerHeight + document.documentElement.scrollTop === document.documentElement.offsetHeight) {
                if (!scrollLimitFound.current) {
                    setOffset(oldOffset => oldOffset + limit);
                }
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [limit, scrollLimitFound]);

    useEffect(() => {
        const fetchClients = async () => {
            const clientResponse = await fetch("/api/v1/quotes?status=" + selectedTab + `&limit=${limit}&offset=${offset}`);
            const clientData = await clientResponse.json();
            var needsReset = false;
            if (oldSelectedTab !== selectedTab) {
                setOldSelectedTab(selectedTab);
                needsReset = true;
                scrollLimitFound.current = false;
                setOffset(0);
            }
            const itemsToAppend = clientData.result || [];
            if (itemsToAppend.length === 0) {
                scrollLimitFound.current = true;
            }
            if (selectedTab === StatusFlowState.paid || selectedTab === StatusFlowState.unpaid || selectedTab === StatusFlowState.delivered) {
                setQuotesDelivered(quotes => mergeQuotesDelivered(needsReset ? [] : quotes, itemsToAppend));
                setQuotes([]);
                setRequests([]);
            } else if (selectedTab === StatusFlowState.requests) {
                setQuotesDelivered([]);
                setQuotes([]);
                setRequests(requests => mergeRequests(needsReset ? [] : requests, itemsToAppend));
            } else {
                setQuotesDelivered([]);
                setQuotes(quotes => mergeQuotes(needsReset ? [] : quotes, itemsToAppend));
                setRequests([]);
            }
            if (clientData.error) {
                setSnackbar(clientData.error);
            }
        };
        fetchClients();
    }, [selectedTab, limit, offset, oldSelectedTab]);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    return (
        <Box>
            <Stack spacing={0}>
                <EkasiServeAppBar />
                <Container>

                    <Grid container spacing={2} sx={{ alignItems: "center" }}>
                        <Grid size={12}> <h1> Quotes </h1> </Grid>

                        <Grid container size={{ xs: 12, md: 11 }} alignItems={"center"} justifyContent={"center"}>
                            <ButtonGroup orientation={ isMobile ? "vertical" : "horizontal" } fullWidth>
                                <Button 
                                    component={Link} to="/home/cancelled"
                                    variant={selectedTab === StatusFlowState.cancelled ? "contained" : "outlined"}> 
                                    Cancelled 
                                </Button>
                                <Button 
                                    component={Link} to="/home/requests"
                                    variant={selectedTab === StatusFlowState.requests ? "contained" : "outlined"}>
                                    Requests
                                </Button>
                                <Button 
                                    component={Link} to="/home/pending"
                                    variant={selectedTab === StatusFlowState.pending ? "contained" : "outlined"}>
                                    Pending PO
                                </Button>
                                <Button 
                                    component={Link} to="/home/processing"
                                    variant={selectedTab === StatusFlowState.processing ? "contained" : "outlined"}>
                                    Processing
                                </Button>
                                <Button 
                                    component={Link} to="/home/delivery"
                                    variant={selectedTab === StatusFlowState.delivery ? "contained" : "outlined"}>
                                    Delivering
                                </Button>
                                <Button 
                                    component={Link} to="/home/partially"
                                    variant={selectedTab === StatusFlowState.partially ? "contained" : "outlined"}>
                                    Partial
                                </Button>
                                {userIsEmployeeOrAdmin(auth) && <Button 
                                    component={Link} to="/home/unpaid"
                                    variant={selectedTab === StatusFlowState.unpaid ? "contained" : "outlined"}>
                                    Unpaid
                                </Button>}
                                {userIsEmployeeOrAdmin(auth) && <Button 
                                    component={Link} to="/home/paid"
                                    variant={selectedTab === StatusFlowState.paid ? "contained" : "outlined"}> 
                                    Paid 
                                </Button>}
                                {!userIsEmployeeOrAdmin(auth) && <Button 
                                    component={Link} to="/home/delivered"
                                    variant={selectedTab === StatusFlowState.delivered ? "contained" : "outlined"}>
                                    Delivered
                                </Button>
                                }
                            </ButtonGroup>
                        </Grid>
                        <Grid size={{ xs: 12, md: 1 }}>
                            <Button 
                                onClick={() => navigate(userIsEmployeeOrAdmin(auth) ? "/quotes/0" : "/requests/0")} 
                                variant="contained" fullWidth> 
                                <Add /> 
                            </Button>
                        </Grid>

                        <Grid size={12}>
                            {
                                (() => {
                                    switch (selectedTab) {
                                        case StatusFlowState.cancelled: return <QuotesCancelledTable quotes={quotes} setQuotes={setQuotes} setSnackbar={setSnackbar}/>;
                                        case StatusFlowState.requests: return userIsEmployeeOrAdmin(auth) ? <RequestsQuoteTable requests={requests}/> : <RequestsTable requests={requests} setRequests={setRequests} setSnackbar={setSnackbar} />;
                                        case StatusFlowState.pending: return <QuotesPendingTable quotes={quotes} setQuotes={setQuotes} setSnackbar={setSnackbar}/>;
                                        case StatusFlowState.processing: return <QuotesProcessingTable quotes={quotes} setQuotes={setQuotes} setSnackbar={setSnackbar}/>;
                                        case StatusFlowState.delivery: return <QuotesDeliveryTable quotes={quotes} taxDates={taxDates} setTaxDates={setTaxDates} setSnackbar={setSnackbar}/>;
                                        case StatusFlowState.partially: return <QuotesPartialTable quotes={quotes} setQuotes={setQuotes} taxDates={taxDates} setTaxDates={setTaxDates} setSnackbar={setSnackbar}/>;
                                        case StatusFlowState.unpaid: return <QuotesUnpaidTable quotesDelivered={quotesDelivered} setQuotesDelivered={setQuotesDelivered} setSnackbar={setSnackbar}/>;
                                        case StatusFlowState.paid: return <QuotesPaidTable quotesDelivered={quotesDelivered} setQuotesDelivered={setQuotesDelivered} setSnackbar={setSnackbar}/>;
                                        case StatusFlowState.delivered: return <QuotesPaidTable quotesDelivered={quotesDelivered} setQuotesDelivered={setQuotesDelivered} setSnackbar={setSnackbar}/>;
                                    }
                                })()
                            }
                        </Grid>
                    </Grid>
                </Container>
                <Snackbar
                    open={snackbar !== ""}
                    autoHideDuration={6000}
                    onClose={(ev, reason) => setSnackbar("")}
                    message={snackbar}/>
            </Stack>
        </Box>
    )
}